import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FileText, Download, Loader2 } from "lucide-react";

const STATUS_FR = {
  paid: { label: "Payée", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  failed: { label: "Échouée", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  open: { label: "En attente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

const money = (cents, cur = "eur") => {
  const sym = { eur: "€", usd: "$", gbp: "£" }[(cur || "eur").toLowerCase()] || "€";
  return `${((cents || 0) / 100).toFixed(2)} ${sym}`;
};

export default function ProInvoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dl, setDl] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pro/invoices");
        setItems(data || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const download = async (inv) => {
    setDl(inv.id);
    try {
      const res = await api.get(`/pro/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `facture-${inv.number || inv.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDl("");
    }
  };

  return (
    <div data-testid="pro-invoices-page">
      <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
        <FileText className="text-[#E05638]" size={26} /> Mes factures
      </h1>
      <p className="text-slate-500 text-sm mt-1">
        Retrouvez et téléchargez les factures de votre abonnement PROXIreduc-28.
      </p>

      <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400 flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" size={18} /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm" data-testid="pro-invoices-empty">
            Aucune facture pour le moment. Vos factures apparaîtront ici dès votre premier paiement d'abonnement.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Numéro</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Période</th>
                <th className="px-4 py-3 font-semibold">Montant</th>
                <th className="px-4 py-3 font-semibold">Statut</th>
                <th className="px-4 py-3 font-semibold text-right">PDF</th>
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => {
                const st = STATUS_FR[inv.status] || { label: inv.status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
                return (
                  <tr key={inv.id} className="border-t border-slate-100" data-testid={`pro-invoice-row-${inv.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{inv.number}</td>
                    <td className="px-4 py-3 text-slate-600">{(inv.paid_at || inv.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {inv.period_start && inv.period_end
                        ? `${inv.period_start.slice(0, 10)} → ${inv.period_end.slice(0, 10)}`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-900">{money(inv.amount, inv.currency)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-semibold ${st.cls}`}>{st.label}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => download(inv)} disabled={dl === inv.id}
                              data-testid={`pro-invoice-dl-${inv.id}`}>
                        {dl === inv.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                        <span className="ml-1">PDF</span>
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
